export const BATTLEFIELD_SLOT_ZONES = new Set([
  'opponent_front',
  'opponent_flank',
  'your_front',
  'your_flank',
]);

/** Viewer-relative zone name → owner player id (your_* = viewer, opponent_* = other player). */
export function resolveViewerSlotOwnerPlayerId(zoneName, gameState, viewerUserId) {
  if (!zoneName || !gameState?.state) {
    return null;
  }
  const allPlayerIds = gameState.players || Object.keys(gameState.state);
  if (zoneName.startsWith('your_')) {
    if (viewerUserId != null && gameState.state[viewerUserId]) {
      return String(viewerUserId);
    }
    return allPlayerIds[0] || null;
  }
  if (zoneName.startsWith('opponent_')) {
    if (viewerUserId != null) {
      const opponentId = allPlayerIds.find((id) => String(id) !== String(viewerUserId));
      if (opponentId) {
        return String(opponentId);
      }
    }
    return allPlayerIds.length > 1 ? allPlayerIds[1] : (allPlayerIds[0] || null);
  }
  return null;
}

/** True when instanceId still occupies any front/flank slot for that player (reposition, not death). */
export function isUnitStillOnBattlefield(gameState, playerId, instanceId) {
  if (!gameState?.state || !playerId || !instanceId) {
    return false;
  }
  const playedOrders = gameState.state[playerId]?.playedOrders;
  if (!playedOrders) {
    return false;
  }
  return ['front', 'flank'].some((zone) =>
    playedOrders[zone]?.some((unit) => unit && unit.instanceId === instanceId)
  );
}

/**
 * Resolve gameState.state player key for deploy/strategem lethal checks.
 * Callers often pass viewer-relative `your` / `opponent`; zone + viewer id is authoritative.
 */
export function resolveAuthoritativeTargetOwnerPlayerId({
  zoneName,
  gameState,
  viewerUserId,
  targetOwnerPlayerId = null,
}) {
  const fromZone = resolveViewerSlotOwnerPlayerId(zoneName, gameState, viewerUserId);
  if (fromZone) {
    return fromZone;
  }
  if (targetOwnerPlayerId == null || targetOwnerPlayerId === '') {
    return null;
  }
  const pid = String(targetOwnerPlayerId);
  if (pid === 'your' || pid === 'opponent') {
    return null;
  }
  return pid;
}

/**
 * Whether a strategem / deploy hit on a battlefield unit should use the lethal white-fade sequence.
 */
export function isBattlefieldUnitLethalHit({
  zoneName,
  index,
  targetInstanceId,
  destroysUnits = false,
  damageAmount = null,
  healthAfter = null,
  unitSnapshot = null,
  slotHasUnit = null,
  gameState = null,
  viewerUserId = null,
  targetOwnerPlayerId = null,
  isDecommissionedUnitInHand = null,
}) {
  if (zoneName == null || index == null || !BATTLEFIELD_SLOT_ZONES.has(zoneName)) {
    return false;
  }
  if (destroysUnits) {
    return true;
  }
  if (typeof healthAfter === 'number' && healthAfter > 0) {
    return false;
  }
  if (typeof unitSnapshot?.health === 'number' && typeof damageAmount === 'number' && damageAmount >= unitSnapshot.health) {
    return true;
  }
  if (
    targetInstanceId &&
    typeof slotHasUnit === 'function' &&
    !slotHasUnit(zoneName, index, targetInstanceId)
  ) {
    const ownerPlayerId = resolveAuthoritativeTargetOwnerPlayerId({
      zoneName,
      gameState,
      viewerUserId,
      targetOwnerPlayerId,
    });
    if (gameState && ownerPlayerId && targetInstanceId) {
      if (isUnitStillOnBattlefield(gameState, ownerPlayerId, targetInstanceId)) {
        return false;
      }
    }
    if (
      typeof isDecommissionedUnitInHand === 'function'
      && gameState
      && ownerPlayerId
      && unitSnapshot
    ) {
      const hand = gameState.state?.[ownerPlayerId]?.hand;
      if (isDecommissionedUnitInHand(hand, unitSnapshot)) {
        return false;
      }
    }
    return true;
  }
  return false;
}

export function findBattlefieldSlotElementByInstanceId(instanceId) {
  if (instanceId == null || instanceId === '' || typeof document === 'undefined') {
    return null;
  }
  const suffix = `_${String(instanceId)}`;
  const nodes = document.querySelectorAll('[data-unit-key]');
  for (let i = 0; i < nodes.length; i += 1) {
    const key = nodes[i].getAttribute('data-unit-key') || '';
    if (key.endsWith(suffix)) {
      return nodes[i];
    }
  }
  const byInstanceId = document.querySelector(`[data-instance-id="${String(instanceId)}"]`);
  if (byInstanceId) {
    return byInstanceId.closest('[data-slot-zone]') || byInstanceId;
  }
  return null;
}

export function getSlotDamageAnchorFromDom(zoneName, index, instanceId = null) {
  if (zoneName == null || index == null) {
    return null;
  }
  let el = null;
  if (instanceId != null && instanceId !== '') {
    el = findBattlefieldSlotElementByInstanceId(instanceId);
  }
  if (!el) {
    el = document.querySelector(`[data-slot-zone="${zoneName}"][data-slot-index="${index}"]`);
  }
  if (!el) {
    return null;
  }
  const cardBounds = el.querySelector('.order-design-card-bounds') || el.querySelector('.order-design-minimal');
  const anchorEl = cardBounds || el;
  const rect = anchorEl.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    slotElement: el,
    rect,
  };
}

/**
 * Whether a queued damage trigger targets a general (HUD portrait) vs a unit card.
 * Must match BattleUI damage-animation render and invalid-anchor fallback logic.
 */
export function resolveDamageTriggerIsGeneralAnimation(attackId, trigger) {
  if (attackId?.includes('general')) {
    return true;
  }
  if (trigger?.isGeneralAnimation === true) {
    return true;
  }
  const slotZone = trigger?.slotElement?.getAttribute?.('data-zone');
  return typeof slotZone === 'string' && slotZone.includes('general');
}

/** Match DamageAnimation render bounds — invalid anchors must skip the pop and release via countdown. */
export function isValidDamageAnimationAnchor(anchor, { isGeneralAnimation = false } = {}) {
  if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') {
    return false;
  }
  const { x, y } = anchor;
  const minY = isGeneralAnimation ? 0 : 50;
  if (Number.isNaN(x) || Number.isNaN(y) || x <= 0 || y < minY || x > 3000 || y > 2000 || x < 50) {
    return false;
  }
  return true;
}

/** Resolve a deployed unit on a viewer-relative battlefield slot. */
/** Post-damage HP for lethal-death ghost cards (never show pre-hit HP on a dying unit). */
export function postDamageHealthFromSnapshot(unitSnapshot, damageAmount) {
  if (!unitSnapshot || typeof unitSnapshot.health !== 'number') {
    return 0;
  }
  if (typeof damageAmount === 'number' && damageAmount > 0) {
    return Math.max(0, unitSnapshot.health - damageAmount);
  }
  return 0;
}

export function getBattlefieldUnitAtZoneIndex(gameState, viewerUserId, opponentId, zoneName, index) {
  if (!gameState?.state || zoneName == null || index == null || !BATTLEFIELD_SLOT_ZONES.has(zoneName)) {
    return null;
  }
  const zone = zoneName.includes('_front') ? 'front' : zoneName.includes('_flank') ? 'flank' : null;
  if (!zone) return null;
  const playerId = zoneName.startsWith('your_') ? viewerUserId : opponentId;
  if (!playerId) return null;
  const unit = gameState.state[playerId]?.playedOrders?.[zone]?.[index];
  return unit && unit.instanceId ? unit : null;
}
